import { CollectionState, ElectionType } from '../../api/api-types';
import {
  SchemaFrontendUserDataViewModel,
  SchemaUserDeclaration,
} from '../../api/api-schema';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';

export const formatDate = (date: string | null | undefined) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const getDaysLeftText = (declaration: SchemaUserDeclaration) => {
  let timeLeft = declaration.daysLeft ?? 0;
  const timeUnit = timeLeft === 1 ? 'dag' : 'dage';

  if (declaration.daysLeft === 1) {
    const initiationDate = new Date(declaration.dateOfInitiation ?? '');
    const currentTime = new Date().getTime();

    const deadlineTime = initiationDate.setHours(
      initiationDate.getHours() + 7 * 24,
    );
    timeLeft = Math.max(
      0,
      Math.ceil((deadlineTime - currentTime) / (1000 * 60)),
    );

    if (timeLeft < 60) {
      return `Bekræft om ${timeLeft} minut${timeLeft !== 1 ? 'ter' : ''}`;
    }

    timeLeft = Math.ceil(timeLeft / 60);
    return `Bekræft om ${timeLeft} time${timeLeft > 1 ? 'r' : ''}`;
  }

  return `Bekræft om ${timeLeft} ${timeUnit}`;
};

export const getElectionLabel = (
  electionType: ElectionType | undefined,
  labels?: {
    dkElectionLabel?: string;
    euElectionLabel?: string;
    bothElectionLabel?: string;
  },
) => {
  if (electionType === ElectionType.EuropaParlamentsvalg) {
    return labels?.euElectionLabel ?? 'Europa-Parlamentsvalg';
  }

  if (electionType === ElectionType.BothTypes) {
    return (
      labels?.bothElectionLabel ?? 'Folketingsvalg / Europa-Parlamentsvalg'
    );
  }

  return labels?.dkElectionLabel ?? 'Folketingsvalg';
};

const electionOrder = {
  [ElectionType.Folketingsvalg]: 0,
  [ElectionType.EuropaParlamentsvalg]: 1,
  [ElectionType.BothTypes]: 2,
};

export const sortDeclarationsByElection = (
  declarations: SchemaUserDeclaration[] = [],
) =>
  [...declarations].sort((a, b) => {
    return (
      electionOrder[a.electionType ?? ElectionType.BothTypes] -
      electionOrder[b.electionType ?? ElectionType.BothTypes]
    );
  });

export const canWithdrawDeclaration = (declaration: SchemaUserDeclaration) => {
  if (
    declaration.collectionState === CollectionState.approved ||
    declaration.collectionState === CollectionState.locked
  ) {
    return !declaration.isConfirmed;
  }

  return true;
};

export const canConfirmDeclaration = (declaration: SchemaUserDeclaration) =>
  declaration.collectionState !== CollectionState.approved &&
  declaration.collectionState !== CollectionState.deactivated &&
  declaration.collectionState !== CollectionState.locked;

export const getDeclarationsToConfirm = (
  declarations: SchemaUserDeclaration[] = [],
) =>
  sortDeclarationsByElection(
    declarations.filter(
      (declaration) =>
        declaration.daysLeft === 0 &&
        !declaration.isConfirmed &&
        canConfirmDeclaration(declaration),
    ),
  );

export const getConfirmAction = (
  declarations: SchemaUserDeclaration[] = [],
) => {
  const declarationsToConfirm = getDeclarationsToConfirm(declarations);
  const partyIds = declarationsToConfirm
    .map((declaration) => declaration.partyId)
    .filter(Boolean)
    .join(',');
  const electionType =
    declarationsToConfirm.length === 1
      ? declarationsToConfirm[0]?.electionType
      : undefined;

  return {
    declarationsToConfirm,
    electionType,
    hasConfirmCta: declarationsToConfirm.length > 0,
    partyIds,
  };
};

export const getConfirmElectionType = (
  declarations: SchemaUserDeclaration[] = [],
) => getConfirmAction(declarations).electionType;

export const getConfirmPartyIds = (
  declarations: SchemaUserDeclaration[] = [],
) => getConfirmAction(declarations).partyIds;

export const hasRightsWarning = (
  user: SchemaFrontendUserDataViewModel | undefined,
) =>
  !!user &&
  ((!user.votingRights?.dk && !!user.votingRights?.eu) ||
    (!user.votingRights?.eu && !!user.votingRights?.dk) ||
    (!user.votingRights?.dk && !user.votingRights?.eu));

type StartEligibilityInput = {
  collectionState: CollectionState;
  electionType?: ElectionType;
  partyId?: string;
  user?: SchemaFrontendUserDataViewModel;
  dkRunning?: boolean;
  euRunning?: boolean;
};

export type StartDeclarationTarget = {
  action: DeclarationActionTypes;
  electionType?: ElectionType;
  partyId?: string;
};

const electionSlots = [
  {
    electionType: ElectionType.Folketingsvalg,
    votingRight: 'dk',
    isRunning: ({ dkRunning }: StartEligibilityInput) => !!dkRunning,
  },
  {
    electionType: ElectionType.EuropaParlamentsvalg,
    votingRight: 'eu',
    isRunning: ({ euRunning }: StartEligibilityInput) => !!euRunning,
  },
] as const;

const getStartableElectionTypes = (
  input: StartEligibilityInput,
): ElectionType[] => {
  const { collectionState, user } = input;
  if (collectionState !== CollectionState.running) return [];
  if (!user) {
    return input.dkRunning && input.euRunning
      ? [ElectionType.Folketingsvalg, ElectionType.EuropaParlamentsvalg]
      : [input.electionType ?? ElectionType.BothTypes];
  }

  return electionSlots
    .filter((slot) => {
      const hasDeclaration = user.userDeclarations?.some(
        (declaration) => declaration.electionType === slot.electionType,
      );

      return (
        slot.isRunning(input) &&
        !!user.votingRights?.[slot.votingRight] &&
        !hasDeclaration
      );
    })
    .map((slot) => slot.electionType);
};

export const getStartEligibility = ({
  collectionState,
  electionType,
  partyId,
  user,
  dkRunning,
  euRunning,
}: StartEligibilityInput) => {
  const isDualCollectionParty = !!dkRunning && !!euRunning;
  const existingDeclaration = user?.userDeclarations?.[0];
  const hasOneDeclarationForAnotherParty =
    user?.userDeclarations?.length === 1 &&
    !!partyId &&
    existingDeclaration !== undefined &&
    existingDeclaration?.partyId !== partyId &&
    canWithdrawDeclaration(existingDeclaration);
  const hasDeclarationForCurrentPartyAndElection =
    !!partyId &&
    user?.userDeclarations?.some(
      (declaration) =>
        declaration.partyId === partyId &&
        declaration.electionType === electionType,
    );
  const startableElectionTypes = getStartableElectionTypes({
    collectionState,
    electionType,
    partyId,
    user,
    dkRunning,
    euRunning,
  });
  const scopedStartableElectionTypes =
    isDualCollectionParty && hasDeclarationForCurrentPartyAndElection
      ? startableElectionTypes.filter((type) => type === electionType)
      : startableElectionTypes;
  const shouldStartBoth =
    isDualCollectionParty &&
    (scopedStartableElectionTypes.length === 2 ||
      (hasOneDeclarationForAnotherParty &&
        scopedStartableElectionTypes.length === 1));

  return {
    canStartDeclaration: scopedStartableElectionTypes.length > 0,
    initiateElectionType: shouldStartBoth
      ? ElectionType.BothTypes
      : scopedStartableElectionTypes[0],
  };
};

export const getStartDeclarationTargets = ({
  dkRunning,
  euRunning,
  initiateElectionType,
  partyId,
  user,
}: {
  dkRunning?: boolean;
  euRunning?: boolean;
  initiateElectionType?: ElectionType;
  partyId?: string;
  user?: SchemaFrontendUserDataViewModel;
}): StartDeclarationTarget[] | undefined => {
  if (!user) return undefined;

  const targets: StartDeclarationTarget[] = [];

  if (initiateElectionType !== ElectionType.BothTypes) {
    targets.push({
      action: DeclarationActionTypes.initiate,
      electionType: initiateElectionType,
      partyId,
    });
  }

  if (!dkRunning || !euRunning) return targets;

  const electionTypesToCheck =
    initiateElectionType === ElectionType.BothTypes
      ? [ElectionType.Folketingsvalg, ElectionType.EuropaParlamentsvalg]
      : initiateElectionType !== undefined
      ? [initiateElectionType]
      : [];

  electionTypesToCheck.forEach((electionType) => {
    const declaration = user.userDeclarations?.find(
      (candidate) => candidate.electionType === electionType,
    );

    if (!declaration || !canWithdrawDeclaration(declaration)) return;

    targets.push({
      action: DeclarationActionTypes.withdraw,
      electionType: declaration.electionType,
      partyId: declaration.partyId,
    });
  });

  return targets;
};
